// src/lib/report.js
import { daysBetween, average } from "../helpers/utils";
import { getOpenAIMessage } from "../chatgpt/aiClient";
import { normalizeAI, safeParseJSON } from "../chatgpt/helpers";

/**
 * Expects:
 * - asanaData items:
 *   { id, title, owner, ownerEmail, createdAt, completedAt, dueDate, completed, project, notes }
 * - slackData items (from /api/slack/messages):
 *   { ts, user_id, user_name, user_email, text, date, channel }
 * - teamMembers items (optional, for stronger matching):
 *   { name, email }
 *
 * New optional params:
 * - aiRisks: array of risk objects (from detectRisksWithAI). If not provided, no risks shown.
 * - aiError: optional string; if present, summary/plaintext includes a friendly notice.
 */
export function compileWeeklyReport({
  asanaConnected: _asanaConnected,
  asanaData,
  slackData = [],
  teamMembers = [],
  aiRisks = [],
  aiError = null,
  overdueDaysThreshold,
  // keeping this param for signature continuity; not used without cost fields
  riskAmountThreshold,
}) {
  const now = new Date();
  const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
  const hasTaskData = Array.isArray(asanaData) && asanaData.length > 0;
  const hasMessageData = Array.isArray(slackData) && slackData.length > 0;

  // --- Guard: no usable task or message data ---
  if (!hasTaskData && !hasMessageData) {
    const summaryBase =
      "Connect task or chat tools to generate KPIs, risks, and decisions. BossDrop will analyze tasks, due dates, and team updates automatically.";
    const summary = aiError
      ? `${summaryBase} (AI risk analysis unavailable right now — please try again.)`
      : summaryBase;

    const kpis = {
      issuesClosed: 0,
      leadTimeDays: 0,
      onTimePct: 0,
      onTimeTasksPct: 0,
    };

    const risks = Array.isArray(aiRisks) ? aiRisks : [];
    const plaintext = renderPlaintext({
      summary,
      kpis,
      risks,
      decisions: [],
      aiError,
    });
    return { summary, kpis, risks, decisions: [], plaintext };
  }

  if (!hasTaskData && hasMessageData) {
    const recentMessages = (slackData || []).filter((m) => {
      const ts = m?.date ? new Date(m.date).getTime() : 0;
      return ts && ts >= weekAgo.getTime() && ts <= now.getTime();
    });
    const participantCount = new Set(
      recentMessages
        .map((m) => String(m.user_email || m.user_name || "").trim().toLowerCase())
        .filter(Boolean),
    ).size;
    const blockerCount = recentMessages.filter((m) =>
      /\b(blocked|stuck|at risk|waiting on|need help|approval|dependency|late|slip)\b/i.test(
        String(m.text || ""),
      ),
    ).length;

    const summaryParts = [
      `${recentMessages.length || slackData.length} recent team updates were available`,
    ];
    if (participantCount) summaryParts.push(`from ${participantCount} teammates`);
    summaryParts.push("but no task system data was available for KPI math");
    if (blockerCount) {
      summaryParts.push(`${blockerCount} updates mention blockers or waiting work`);
    }
    if (aiError) summaryParts.push("AI risk analysis was unavailable");

    const summary = `${summaryParts.filter(Boolean).join(". ")}.`;
    const kpis = {
      issuesClosed: 0,
      leadTimeDays: 0,
      onTimePct: 0,
      onTimeTasksPct: 0,
    };
    const risks = Array.isArray(aiRisks) ? aiRisks : [];
    const plaintext = renderPlaintext({
      summary,
      kpis,
      risks,
      decisions: [],
      aiError,
    });
    return { summary, kpis, risks, decisions: [], plaintext };
  }

  // Helpers
  const toDate = (d) => (d ? new Date(d) : null);
  // Asana tasks carry camelCase dates; Linear, ClickUp, Notion, monday.com and
  // Jira tasks carry snake_case ones. Read either, or those tools never count.
  const completedAtOf = (t) => toDate(t?.completedAt || t?.completed_at);
  const createdAtOf = (t) => toDate(t?.createdAt || t?.created_at);
  const isRelease = (t) =>
    /release/i.test(t?.title || "") || /release/i.test(t?.project || "");

  // --- Closed items this week (KPIs stay sync & deterministic) ---
  const closedThisWeek = asanaData.filter((t) => {
    const doneAt = completedAtOf(t);
    return t.completed && doneAt && doneAt >= weekAgo && doneAt <= now;
  });

  // Lead time = completedAt - createdAt (days)
  const leadTimes = closedThisWeek
    .map((t) => {
      const created = createdAtOf(t);
      const completed = completedAtOf(t);
      if (!created || !completed) return null;
      return (completed - created) / (24 * 60 * 60 * 1000);
    })
    .filter((x) => Number.isFinite(x) && x >= 0);

  const leadTimeDays = leadTimes.length ? average(leadTimes) : 0;

  // --- On-time metrics ---
  // A date-only due date ("2026-10-18") means "by the end of that day", in
  // the viewer's local time. toDate() alone reads it as midnight UTC, which
  // counted every task finished on its due day as late.
  const dueEndOf = (value) => {
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(value || "").trim());
    if (!m) return toDate(value);
    return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]), 23, 59, 59, 999);
  };
  const closedWithDue = closedThisWeek.filter((t) => !!t.dueDate);
  const onTimeTasks = closedWithDue.filter((t) => {
    const due = dueEndOf(t.dueDate);
    const done = completedAtOf(t);
    return due && done && done <= due;
  });
  const onTimeTasksPct = closedWithDue.length
    ? (100 * onTimeTasks.length) / closedWithDue.length
    : 0;

  // On-time releases (heuristic, KPI only)
  const releasesThisWeek = closedThisWeek.filter(isRelease);
  const releasesWithDue = releasesThisWeek.filter((t) => !!t.dueDate);
  const onTimeReleases = releasesWithDue.filter((t) => {
    const due = dueEndOf(t.dueDate);
    const done = completedAtOf(t);
    return due && done && done <= due;
  });
  const onTimePct = releasesWithDue.length
    ? (100 * onTimeReleases.length) / releasesWithDue.length
    : 0;

  // --- Risks: AI ONLY ---
  const risks = Array.isArray(aiRisks) ? aiRisks.slice() : [];

  // Sort combined risks (if any were provided)
  risks.sort(
    (a, b) =>
      (b.severity || 0) - (a.severity || 0) ||
      (b.ageDays || 0) - (a.ageDays || 0)
  );

  const issuesClosed = closedThisWeek.length;

  const summary = (() => {
    const parts = [];
    parts.push(
      `Delivery: ${issuesClosed} tasks closed this week; lead time ${leadTimeDays.toFixed(
        1
      )}d`
    );
    parts.push(`On-time tasks ${Math.round(onTimeTasksPct)}%`);
    if (releasesThisWeek.length) {
      parts.push(`On-time releases ${Math.round(onTimePct)}%`);
    }
    if (aiError) {
      parts.push("AI risk analysis unavailable — showing KPIs only.");
    } else if (risks.length) {
      const slackCount = risks.filter((r) => r.source === "slack").length;
      const label =
        slackCount > 0
          ? `${risks.length} AI-identified risks (${slackCount} from Slack)`
          : `${risks.length} AI-identified risks`;
      parts.push(`${label} (top: ${risks[0].title}).`);
    } else {
      parts.push("No AI-identified risks.");
    }
    return parts.join(". ") + ".";
  })();

  const kpis = {
    issuesClosed,
    leadTimeDays,
    onTimePct, // releases heuristic KPI
    onTimeTasksPct,
  };

  const plaintext = renderPlaintext({
    summary,
    kpis,
    risks,
    decisions: [],
    aiError,
  });

  return {
    summary,
    kpis,
    risks,
    decisions: [],
    plaintext,
  };
}

/* ===================== AI RISK DETECTION (ONLY) ===================== */
/**
 * Call this from App.jsx (after you have asanaData & slackData).
 * Returns { risks: Risk[], error: string|null }
 *
 * Example wiring in App.jsx:
 *   const [aiRisks, setAiRisks] = useState([]);
 *   const [aiError, setAiError] = useState(null);
 *   useEffect(() => {
 *     (async () => {
 *       try {
 *         const { risks, error } = await detectRisksWithAI({ asanaData, slackData, teamMembers });
 *         setAiRisks(risks);
 *         setAiError(error);
 *       } catch (e) {
 *         setAiRisks([]);
 *         setAiError("AI risk analysis failed.");
 *       }
 *     })();
 *   }, [asanaData, slackData, teamMembers]);
 *   // …then pass aiRisks, aiError into compileWeeklyReport(...)
 */
export async function detectRisksWithAI({
  asanaData = [],
  slackData = [],
  teamMembers = [],
}) {
  try {
    const nowISO = new Date().toISOString();

    // Prepare compact inputs for the model
    const slimAsana = (asanaData || []).slice(0, 400).map((t) => ({
      id: String(t.id),
      title: String(t.title || ""),
      owner: String(t.owner || ""),
      ownerEmail: t.ownerEmail || null,
      createdAt: t.createdAt || t.created_at || null,
      dueDate: t.dueDate || null,
      completed: !!t.completed,
      notes: t.notes ? String(t.notes).slice(0, 400) : "",
      project: String(t.project || ""),
    }));

    const slimSlack = (slackData || []).slice(0, 400).map((m) => ({
      ts: String(m.ts || ""),
      user_name: String(m.user_name || m.user_id || "user"),
      user_email: m.user_email || null,
      text: String(m.text || ""),
      date: m.date || null,
      channel: String(m.channel || ""),
    }));

    const prompt =
      `You are helping an engineering/product team identify execution risks from Slack messages and Asana tasks.\n` +
      `Today is ${nowISO}.\n\n` +
      `Return STRICT JSON ONLY with this exact schema:\n` +
      `{"asana":[{"id":"<asana task id>","severity":1|2,"reason":"<short reason>"}],` +
      `"slack":[{"ts":"<slack ts>","severity":1|2,"reason":"<short reason>"}]}\n\n` +
      `Rules:\n` +
      `- severity=2 for blockers, outages, past-due commitments, critical dependencies; severity=1 for delays, waiting, approvals, risks.\n` +
      `- Prefer precision; only include items that clearly indicate risk.\n` +
      `- Keep "reason" concise (≤ 16 words).\n` +
      `- Do not include completed Asana tasks unless the message explicitly states a post-completion issue.\n\n` +
      `Asana (subset):\n` +
      JSON.stringify(slimAsana) +
      `\n\nSlack (subset):\n` +
      JSON.stringify(slimSlack);

    const raw = await getOpenAIMessage(prompt);
    const text = normalizeAI(raw);
    const parsed = safeParseJSON(text);

    if (!parsed || typeof parsed !== "object") {
      return { risks: [], error: "AI response was not valid JSON." };
    }

    const aiAsana = Array.isArray(parsed.asana) ? parsed.asana : [];
    const aiSlack = Array.isArray(parsed.slack) ? parsed.slack : [];

    const now = new Date();
    const risks = [];

    // Asana risks from AI
    for (const r of aiAsana) {
      const t = slimAsana.find((x) => String(x.id) === String(r.id));
      if (!t) continue;
      const due = t.dueDate ? new Date(t.dueDate) : null;
      const created = t.createdAt ? new Date(t.createdAt) : null;
      const ageDays =
        due && due < now
          ? daysBetween(due, now) // past-due age
          : created
          ? daysBetween(created, now)
          : 0;

      const { owner, ownerEmail } = resolveOwnerFromAsanaTask(t, teamMembers);

      risks.push({
        id: String(t.id),
        title:
          (r.severity === 2 ? `${t.title} (critical)` : t.title) +
          (r.reason ? ` — ${truncate(String(r.reason), 60)}` : ""),
        owner,
        ownerEmail,
        ageDays,
        action:
          r.severity === 2
            ? `Escalate — ${owner} to clear blocker within 24h`
            : `Follow up — ${owner} to update status by EOD`,
        severity: r.severity === 2 ? 2 : 1,
        source: "asana",
        project: t.project,
      });
    }

    // Slack risks from AI
    for (const r of aiSlack) {
      const m = slimSlack.find((x) => String(x.ts) === String(r.ts));
      if (!m) continue;
      const when = m.date ? new Date(m.date) : null;
      const ageDays = when ? daysBetween(when, now) : 0;

      const { owner, ownerEmail } = resolveOwnerFromSlack(
        { user_name: m.user_name, user_email: m.user_email },
        asanaData,
        teamMembers
      );

      risks.push({
        id: `slack:${m.channel}:${m.ts}`,
        title:
          (r.severity === 2 ? `Blocked: ` : `Risk: `) +
          truncate(m.text, 120) +
          (r.reason ? ` — ${truncate(String(r.reason), 60)}` : ""),
        owner,
        ownerEmail,
        ageDays,
        action:
          r.severity === 2
            ? `Unblock — ${owner} to resolve within 24h`
            : `Follow up — ${owner} to update by EOD`,
        severity: r.severity === 2 ? 2 : 1,
        source: "slack",
        channel: m.channel,
      });
    }

    // Deduplicate near-duplicates by (owner/email + title)
    const seen = new Set();
    const deduped = [];
    for (const r of risks) {
      const key = `${(r.ownerEmail || r.owner || "").toLowerCase()}::${
        r.title
      }`;
      if (seen.has(key)) continue;
      seen.add(key);
      deduped.push(r);
    }

    // Sort
    deduped.sort(
      (a, b) =>
        (b.severity || 0) - (a.severity || 0) ||
        (b.ageDays || 0) - (a.ageDays || 0)
    );

    return { risks: deduped, error: null };
  } catch (e) {
    return { risks: [], error: "AI risk analysis failed. Please try again." };
  }
}

/* ===================== NAME-FIRST OWNER MATCHING ===================== */

function norm(s) {
  return (s || "").trim().toLowerCase();
}

// Prefer Team Directory canonical name/email; then fall back to provided values
function resolveOwnerFromAsanaTask(task, teamMembers) {
  const email = norm(task?.ownerEmail);
  const name = norm(task?.owner);

  // 1) Team by email
  if (email) {
    const tm = (teamMembers || []).find((p) => norm(p.email) === email);
    if (tm)
      return { owner: tm.name || task.owner || tm.email, ownerEmail: tm.email };
  }

  // 2) Team by name
  if (name) {
    const tm = (teamMembers || []).find((p) => norm(p.name) === name);
    if (tm)
      return {
        owner: tm.name,
        ownerEmail: tm.email || task.ownerEmail || null,
      };
  }

  // 3) Fallback to Asana values
  return {
    owner: task?.owner || "Unassigned",
    ownerEmail: task?.ownerEmail || null,
  };
}

/**
 * Returns { owner, ownerEmail } for Slack user — name-first, then email.
 * Uses Team Directory (preferred) and Asana roster as a backstop.
 */
function resolveOwnerFromSlack(
  { user_name, user_email },
  asanaData,
  teamMembers
) {
  const n = norm(user_name);
  const e = norm(user_email);

  // 1) Team Directory by name
  const tmByName = (teamMembers || []).find((p) => norm(p.name) === n);
  if (tmByName) {
    return {
      owner: tmByName.name,
      ownerEmail: tmByName.email || user_email || null,
    };
  }

  // 2) Asana by name
  const asByName = (asanaData || []).find((t) => norm(t.owner) === n);
  if (asByName) {
    return {
      owner: asByName.owner,
      ownerEmail: asByName.ownerEmail || user_email || null,
    };
  }

  // 3) Team Directory by email
  if (e) {
    const tmByEmail = (teamMembers || []).find((p) => norm(p.email) === e);
    if (tmByEmail) {
      return {
        owner: tmByEmail.name || user_name || tmByEmail.email,
        ownerEmail: tmByEmail.email,
      };
    }
  }

  // 4) Asana by email
  if (e) {
    const asByEmail = (asanaData || []).find((t) => norm(t.ownerEmail) === e);
    if (asByEmail) {
      return {
        owner: asByEmail.owner || user_name || user_email,
        ownerEmail: asByEmail.ownerEmail || user_email || null,
      };
    }
  }

  // 5) Fallback
  return { owner: user_name || "Unassigned", ownerEmail: user_email || null };
}

/* ===================== Plaintext Renderer ===================== */

export function renderPlaintext({ summary, kpis, risks, decisions, aiError }) {
  const lines = [];
  lines.push(`Executive Summary`);
  lines.push(summary);
  lines.push("");
  lines.push(`KPIs`);
  lines.push(`• Issues closed: ${kpis.issuesClosed}`);
  lines.push(`• Lead time: ${kpis.leadTimeDays.toFixed(1)} days`);
  lines.push(`• On-time releases: ${Math.round(kpis.onTimePct)}%`);
  lines.push(`• On-time tasks: ${Math.round(kpis.onTimeTasksPct)}%`);
  lines.push("");
  lines.push(`Risks & Owners`);
  if (aiError) {
    lines.push(`• AI risk analysis unavailable right now — please try again.`);
  } else if (risks.length === 0) {
    lines.push("• No AI-identified risks");
  } else {
    for (const r of risks) {
      const src = r.source === "slack" ? " (Slack)" : "";
      lines.push(`• ${r.title}${src} — Owner: ${r.owner} — ${r.ageDays}d old`);
    }
  }
  lines.push("");
  lines.push("Decisions Needed");
  if (!decisions || decisions.length === 0) lines.push("• None");
  else decisions.forEach((d) => lines.push(`• ${d}`));
  return lines.join("\n");
}

/* ===================== Utils ===================== */

function truncate(s, n) {
  return s.length <= n ? s : s.slice(0, n - 1) + "…";
}

export default compileWeeklyReport;
